import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { cn } from "@strivelabs/ui-core";
import { ChevronDown } from "lucide-react";
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "motion/react";
import { useComposer, useActiveMode, useModeFraction } from "./context";
import {
  COMPOSER_MODE_GLOWS,
  COMPOSER_MODE_GRADIENT,
  sampleRamp,
} from "./tones";
import { Popover, Tooltip } from "@strivelabs/ui-core/v2";

/** Inner padding of the track, in pixels, that the fill and thumb sit within. */
const MODE_TRACK_PAD = 4;
/** Seconds for the slow, smooth glide when a mode is selected. */
const MODE_GLIDE_SECONDS = 0.55;
const MODE_GLIDE_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

/**
 * Sparkle positions and timings for the fill's glitter, spread across the
 * track so the twinkles stagger rather than pulse in unison.
 */
const MODE_SPARKLES = [
  { left: "6%", top: "34%", size: 2, delay: 0, duration: 1.5 },
  { left: "15%", top: "68%", size: 1.5, delay: 0.7, duration: 1.8 },
  { left: "24%", top: "24%", size: 2.5, delay: 0.3, duration: 1.4 },
  { left: "33%", top: "60%", size: 2, delay: 1.1, duration: 1.7 },
  { left: "42%", top: "40%", size: 1.5, delay: 0.5, duration: 1.6 },
  { left: "51%", top: "72%", size: 2.5, delay: 0.9, duration: 1.9 },
  { left: "60%", top: "30%", size: 2, delay: 0.2, duration: 1.5 },
  { left: "69%", top: "58%", size: 1.5, delay: 1.3, duration: 1.7 },
  { left: "78%", top: "38%", size: 2.5, delay: 0.6, duration: 1.6 },
  { left: "87%", top: "66%", size: 2, delay: 1, duration: 1.8 },
  { left: "94%", top: "32%", size: 1.5, delay: 0.4, duration: 1.5 },
];

/**
 * The Quick → Deep Dive slider: an empty rail with an accent fill that sweeps
 * toward the Deep Dive end and a thumb that travels with it. A continuous
 * position value drives both: dragging tracks the pointer one-to-one, while
 * selecting a mode (click, label, or arrow keys) glides that position slowly
 * across. The thumb springs taller while dragging.
 */
export const ComposerModeSlider = () => {
  const { modes, mode, setMode, disabled } = useComposer();
  const trackRef = useRef<HTMLDivElement>(null);
  const glideRef = useRef<ReturnType<typeof animate> | null>(null);
  const reduceMotion = useReducedMotion();
  const [dragging, setDragging] = useState(false);
  const count = modes.length;
  const activeIndex = Math.max(
    0,
    modes.findIndex((option) => option.id === mode),
  );
  const fractionOf = (index: number) =>
    count > 1 ? index / (count - 1) : 0;

  const position = useMotionValue(fractionOf(activeIndex));
  const left = useTransform(
    position,
    (value) =>
      `calc(${MODE_TRACK_PAD}px + ${value} * (100% - ${MODE_TRACK_PAD * 2}px))`,
  );
  const fillClip = useTransform(
    position,
    (value) => `inset(0 ${(1 - value) * 100}% 0 0)`,
  );
  const glideTo = (fraction: number) => {
    glideRef.current?.stop();
    glideRef.current = animate(
      position,
      fraction,
      reduceMotion
        ? { duration: 0 }
        : { duration: MODE_GLIDE_SECONDS, ease: MODE_GLIDE_EASE },
    );
  };

  useEffect(() => {
    glideTo(fractionOf(activeIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  useEffect(() => () => glideRef.current?.stop(), []);

  const selectIndex = (index: number) => {
    setMode(modes[index].id);
    glideTo(fractionOf(index));
  };

  const pointerFraction = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return fractionOf(activeIndex);
    const rect = el.getBoundingClientRect();
    const inner = rect.width - MODE_TRACK_PAD * 2;
    if (inner <= 0) return 0;
    return Math.max(
      0,
      Math.min(1, (clientX - rect.left - MODE_TRACK_PAD) / inner),
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (count <= 1) return;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      selectIndex(Math.min(activeIndex + 1, count - 1));
    } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      selectIndex(Math.max(activeIndex - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      selectIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      selectIndex(count - 1);
    }
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled || count <= 1) return;
    setDragging(true);
    trackRef.current?.setPointerCapture(event.pointerId);
    glideRef.current?.stop();
    position.set(pointerFraction(event.clientX));
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.buttons !== 1 || disabled || count <= 1) return;
    position.set(pointerFraction(event.clientX));
  };
  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    if (trackRef.current?.hasPointerCapture(event.pointerId)) {
      trackRef.current.releasePointerCapture(event.pointerId);
    }
    if (disabled) return;
    const nearest = Math.max(
      0,
      Math.min(count - 1, Math.round(position.get() * (count - 1))),
    );
    selectIndex(nearest);
  };

  return (
    <div className="px-1 pt-1">
      <div
        ref={trackRef}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label="Response mode"
        aria-valuemin={0}
        aria-valuemax={Math.max(0, count - 1)}
        aria-valuenow={activeIndex}
        aria-valuetext={modes[activeIndex]?.label}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={cn(
          "relative mx-4 h-7 touch-none rounded-[10px] bg-muted/60 shadow-[0_1px_2px_rgba(0,0,0,0.06)] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover",
          disabled ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing",
        )}
      >
        <div className="pointer-events-none absolute inset-1 overflow-hidden rounded-md">
          <motion.div
            className="absolute inset-0"
            style={{ background: COMPOSER_MODE_GRADIENT, clipPath: fillClip }}
          >
            {!reduceMotion && (
              <motion.div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)",
                  backgroundSize: "200% 100%",
                  backgroundRepeat: "no-repeat",
                }}
                animate={{ backgroundPosition: ["200% 0", "-200% 0"] }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 1.5,
                  ease: "linear",
                }}
              />
            )}
            {!reduceMotion &&
              MODE_SPARKLES.map((sparkle, index) => (
                <motion.span
                  key={index}
                  className="absolute rounded-full bg-white"
                  style={{
                    left: sparkle.left,
                    top: sparkle.top,
                    width: sparkle.size,
                    height: sparkle.size,
                    boxShadow: "0 0 3px rgba(255,255,255,0.85)",
                  }}
                  animate={{ opacity: [0, 1, 0], scale: [0.3, 1, 0.3] }}
                  transition={{
                    repeat: Number.POSITIVE_INFINITY,
                    duration: sparkle.duration,
                    delay: sparkle.delay,
                    repeatDelay: 0.4,
                    ease: "easeInOut",
                  }}
                />
              ))}
          </motion.div>
        </div>
        <motion.div
          className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ left }}
        >
          <motion.span
            aria-hidden="true"
            className="block h-5 w-2 rounded-[6px] border border-border bg-background shadow-sm"
            animate={{ scaleY: dragging && !reduceMotion ? 1.14 : 1 }}
            transition={{ type: "spring", stiffness: 500, damping: 14, mass: 0.7 }}
          />
        </motion.div>
      </div>
      <div className="mt-3 flex justify-between">
        {modes.map((option, index) => (
          <button
            key={option.id}
            type="button"
            onClick={() => selectIndex(index)}
            className={cn(
              "rounded px-1 py-0.5 text-xs transition-colors",
              option.id === mode
                ? "font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export interface ComposerModeProps {
  className?: string;
}

/** Mode button showing the active label; opens the slider popover. */
export const ComposerMode = ({ className }: ComposerModeProps) => {
  const { modes, mode, openMenu, setOpenMenu, disabled } = useComposer();
  const active = useActiveMode();
  const fraction = useModeFraction();
  const stepColor = sampleRamp(COMPOSER_MODE_GLOWS, fraction);
  const activeIndex = Math.max(
    0,
    modes.findIndex((option) => option.id === mode),
  );
  const open = openMenu === "mode";

  return (
    <Tooltip label="Response mode" shortcut="⌘M" disabled={open}>
      <Popover
      open={open}
      onOpenChange={(next) => setOpenMenu(next ? "mode" : null)}
      aria-label="Response mode"
      contentClassName="w-[300px]"
      trigger={
        <button
          type="button"
          disabled={disabled}
          aria-label="Response mode"
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            "relative inline-flex h-9 items-center gap-2 rounded-lg bg-transparent px-2.5 text-xs font-medium text-foreground transition-[background-color,transform] before:absolute before:-inset-y-0.5 before:content-[''] hover:bg-accent active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50",
            open && "bg-accent",
            className,
          )}
        >
          <span className="flex items-end gap-[2px]" aria-hidden="true">
            {modes.map((option, index) => (
              <span
                key={option.id}
                className={cn(
                  "w-[3px] rounded-[1px] transition-colors duration-300",
                  index > activeIndex && "bg-border",
                )}
                style={{
                  height: `${5 + index * 3}px`,
                  background:
                    index <= activeIndex ? stepColor : undefined,
                }}
              />
            ))}
          </span>
          <span>{active?.label ?? "Mode"}</span>
          <ChevronDown
            className={cn(
              "size-3.5 text-muted-foreground transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
      }
    >
      <div className="rounded-t-v2-lg border-b border-border bg-muted/60 px-3 py-2.5">
        <div className="text-sm font-medium leading-tight text-foreground">
          Response mode
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          Choose how thorough the response is
        </div>
      </div>
      <div className="p-3">
        <ComposerModeSlider />
        {active?.description && (
          <p className="mt-3 px-1 text-xs leading-snug text-muted-foreground">
            {active.description}
          </p>
        )}
      </div>
      </Popover>
    </Tooltip>
  );
};
